import { useEffect, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import {
  IconArrowLeft,
  IconBriefcase,
  IconClipboard,
  IconLandmark,
  IconMilestone,
  IconOverview,
  IconReceipt,
  IconShieldCheck,
  IconTimeline,
  IconUserDoc,
  IconUserPlus,
} from "../components/Icons";
import { PageHeader } from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { useAppState } from "../context/AppStateContext";
import {
  APP_TAB_PATH_TO_DOSSIER_PERMISSION,
  canMutateApplicantDossiers,
  canSeeDossierMilestonesTab,
} from "../config/applicationHubPermissions";
import { APP_TAB_ROUTES } from "./appTabs";
import { areMilestonesAndBillsUnavailable } from "./milestoneBillsGate";
import { scrollMainToTop } from "../utils/scrollMainToTop";
import { dossierStatusChip } from "./statusUi";

const TAB_ICONS: Record<(typeof APP_TAB_ROUTES)[number]["path"], ReactNode> = {
  overview: <IconOverview className="tab-nav__ico" />,
  beneficiary: <IconUserDoc className="tab-nav__ico" />,
  validation: <IconShieldCheck className="tab-nav__ico" />,
  banking: <IconLandmark className="tab-nav__ico" />,
  subcontractor: <IconBriefcase className="tab-nav__ico" />,
  engineer: <IconUserPlus className="tab-nav__ico" />,
  "work-progress": <IconTimeline className="tab-nav__ico" />,
  "po-register": <IconClipboard className="tab-nav__ico" />,
  milestones: <IconMilestone className="tab-nav__ico" />,
  bills: <IconReceipt className="tab-nav__ico" />,
};

function tabClass({ isActive }: { isActive: boolean }) {
  return "tab-nav__link" + (isActive ? " tab-nav__link--active" : "");
}

export function ApplicationWorkspaceLayout() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { can } = useAuth();
  const { getById } = useAppState();
  const app = id ? getById(id) : undefined;
  const tabSeg = location.pathname.split("/").pop() ?? "overview";
  const activeTab = APP_TAB_ROUTES.find((t) => t.path === tabSeg) ?? APP_TAB_ROUTES[0];

  useEffect(() => {
    scrollMainToTop();
    const frame = requestAnimationFrame(() => scrollMainToTop());
    return () => cancelAnimationFrame(frame);
  }, [id, location.pathname]);

  if (!app) {
    return (
      <div className="workspace workspace--empty">
        <PageHeader
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: "Applicants", to: "/applications" },
            { label: "Not found" },
          ]}
          title="We could not open this file"
          subtitle="The link may be wrong or this reference was removed. Go back to the list and choose another application."
        />
        <NavLink to="/applications" className="btn btn--primary btn--tg">
          Back to applicants
        </NavLink>
      </div>
    );
  }

  const from = (location.state as { from?: string } | null)?.from;
  const base = `/applications/${app.id}`;
  const hideMilestonesBills = areMilestonesAndBillsUnavailable(app);
  const milestonesTabAllowed = canSeeDossierMilestonesTab(can);
  const fullApplicantAccess = can("applications");
  const dossierReadOnly = !canMutateApplicantDossiers(can);

  const APPLICANT_TABS = new Set(["overview", "beneficiary", "validation", "banking"]);
  const BENEFICIARY_TABS = new Set(["subcontractor", "engineer", "work-progress", "po-register", "milestones", "bills"]);

  const sidebarTabs = APP_TAB_ROUTES.filter((t) => {
    if (from === "applicants" && !APPLICANT_TABS.has(t.path)) return false;
    if (from === "beneficiaries" && !BENEFICIARY_TABS.has(t.path)) return false;
    if (hideMilestonesBills && (t.path === "milestones" || t.path === "bills")) return false;
    if (!milestonesTabAllowed && t.path === "milestones") return false;
    if (!fullApplicantAccess) {
      const pk = APP_TAB_PATH_TO_DOSSIER_PERMISSION[t.path];
      if (!can(pk)) return false;
    }
    return true;
  });

  const backTo = from === "beneficiaries" ? "/subcontractor-to-beneficiary" : "/applications";
  const backLabel = from === "beneficiaries" ? "Beneficiaries" : "Applicants";

  return (
    <div className="workspace">
      <aside className="workspace__sidebar" aria-label="Dossier sections">
        <NavLink to={backTo} className="workspace__back">
          <IconArrowLeft />
          <span>{backLabel}</span>
        </NavLink>

        <div className="workspace__dossier">
          <p className="workspace__dossier-label">Beneficiary dossier</p>
          <p className="workspace__dossier-name">{app.mobile.beneficiaryName}</p>
          <p className="workspace__dossier-id mono">{app.mobile.applicationId}</p>
          <p className="workspace__dossier-sanction mono">Sanction: {app.mobile.houseSanctionNumber}</p>
        </div>

        <p className="workspace__nav-title">Sections — pick one</p>
        <p className="workspace__nav-help">
          Only one section shows at a time. Use the links below; the current page is highlighted in green.
        </p>

        <nav className="tab-nav" aria-label="Dossier sections">
          {sidebarTabs.map((tab) => (
            <NavLink
              key={tab.path}
              to={`${base}/${tab.path}`}
              state={location.state}
              className={tabClass}
              end={tab.path === "overview"}
              title={tab.userHint}
            >
              {TAB_ICONS[tab.path]}
              <span className="tab-nav__label">{tab.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="workspace__current-tip" role="status">
          <span className="workspace__current-tip-label">Tip for this screen</span>
          <p>{activeTab.userHint}</p>
        </div>

        <div className="workspace__sidebar-foot">
          <p>
            Telangana State — Chief Minister&apos;s Office (CMO). Official use only. Data shown is mock until backend
            integration.
          </p>
        </div>
      </aside>

      <div className="workspace__main">
        {dossierReadOnly ? (
          <div className="workspace__readonly-banner" role="status">
            View only — this account cannot save changes to applicant dossiers. Ask an administrator if you need edit
            access.
          </div>
        ) : null}
        <PageHeader
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: backLabel, to: backTo },
            { label: app.mobile.applicationId, to: `${base}/overview` },
            { label: activeTab.label },
          ]}
          title={activeTab.label}
          subtitle={
            <>
              <span className="mono text-muted page-header__id-line">
                {app.mobile.applicationId} · Sanction {app.mobile.houseSanctionNumber}
              </span>
              <span className="page-header__tab-desc">{activeTab.desc}</span>
            </>
          }
          badge={dossierStatusChip(app)}
        />
        <Outlet />
      </div>
    </div>
  );
}
